// The map page. Reads the register once, keeps one state, draws from it.

import { createStore, bindUrl, fromHash, DEFAULTS, clearedFilters, activeFilters } from './state.js';
import { applyFilters, parsePlace } from './filters.js';
import { buildIndex, search, textMatcher } from './search.js';
import { createMap, padding } from './map.js';
import { createPanel } from './panel.js';
import { createDrawer } from './drawer.js';
import { createClusterCard } from './cluster.js';
import { createPalette, bindKeys } from './palette.js';
import { createSheet } from './sheet.js';
import { makeT, pageLang, LANGUAGE_NAMES } from './i18n.js';
import { localClock } from './schedule.js';
import { locate, boundsOf, haversineMiles } from './geo.js';
import { markSvg } from './marks.js';

const lang = pageLang();
const t = makeT(lang);
const $ = (id) => document.getElementById(id);

async function boot() {
  const status = $('atlasStatus');
  status.textContent = t('loading');
  let data;
  try {
    const r = await fetch('/api/atlas', { credentials: 'same-origin' });
    if (!r.ok) throw new Error(String(r.status));
    data = await r.json();
    if (!data.tenant) throw new Error('no tenant');
  } catch (err) {
    status.textContent = t('failed');
    status.classList.add('is-error');
    return;
  }
  data.churches = data.churches || [];
  data.places = data.places || [];
  status.hidden = true;
  run(data);
}

function run(data) {
  const tenant = data.tenant;
  const viewportMode = tenant.mode === 'viewport';
  const byId = new Map(data.churches.map((c) => [c.i, c]));
  const clock = (church) => localClock(church?.tz || tenant.tz || Intl.DateTimeFormat().resolvedOptions().timeZone);
  let index = buildIndex(data, t);
  let textMatch = textMatcher(index);
  const filterCtx = () => ({ clock: clock(), clockFor: clock, languageIndex: tenant.languageIndex || null, textMatch });

  const store = createStore(fromHash(location.hash));
  bindUrl(store);
  const mobile = window.matchMedia('(max-width: 767px)');
  let you = null;
  let matched = [], dimmed = [];
  let pendingSel = store.get().sel;

  $('atlasTitle').textContent = tenant.short;
  $('atlasQ').placeholder = t('search.placeholder');
  $('atlasMore').textContent = t('results.more');
  $('atlasHeatNote').textContent = t('layers.heatNote');
  for (const b of document.querySelectorAll('#atlasLayers [data-layer]')) b.textContent = t('layers.' + b.dataset.layer);
  $('atlasKeys').querySelector('h2').textContent = t('keys.title');
  $('atlasStats').textContent = viewportMode ? t('world.count', { n: (tenant.count || 0).toLocaleString(lang) }) : statsLine(data);
  $('atlasLegend').innerHTML = legend();

  const initial = store.get().view;
  const mapApi = createMap($('atlasMap'), {
    center: initial ? [initial[2], initial[1]] : tenant.center,
    zoom: initial ? initial[0] : (tenant.zoom || 10),
    onReady: () => { draw(store.get(), null, true); if (viewportMode) loadViewport(); },
  });
  const map = mapApi.map;
  const panelOpen = () => !document.body.classList.contains('atlas-panel-closed');
  const pad = () => padding(panelOpen() && !mobile.matches, drawer.isOpen() && !mobile.matches, mobile.matches);

  const origin = () => { const s = store.get(); return s.near ? [s.near[0], s.near[1]] : you ? [you.lat, you.lng] : null; };
  const panel = createPanel({
    t, lang, data, store, index, clock, origin, filterCtx,
    search: (q) => search(index, q),
    onQuery: (v) => store.set({ q: v }),
    onPick: pick, onSelect: select, onFit: fit, onLocate: nearMe,
  });
  const drawer = createDrawer({ t, lang, data, store, clock, onClose: () => select(null), onSelect: select });
  const cluster = createClusterCard({ t, mapApi, onSelect: select, onZoom: zoomCluster, onList: listIds });
  const sheet = createSheet({ onSnap: () => map.resize() });
  const tip = $('atlasTip');

  /* ── the render pipeline ─────────────────────────────────────────────── */
  function compute(s) {
    const all = data.churches;
    matched = applyFilters(all, s, filterCtx());
    const seen = new Set(matched.map((c) => c.i));
    dimmed = activeFilters(s).length ? all.filter((c) => !seen.has(c.i)) : [];
    const o = origin();
    if (o) matched.sort((a, b) => haversineMiles(o[0], o[1], a.lat, a.lng) - haversineMiles(o[0], o[1], b.lat, b.lng));
    else matched.sort((a, b) => a.n.localeCompare(b.n));
  }

  const FILTER_KEYS = ['q', 'place', 'near', 'lang', 'day', 'after', 'now', 'hc', 'ha', 'hp', 'lm', 'sc', 'kind'];
  function draw(s, prev, first) {
    const changed = (k) => first || !prev || s[k] !== prev[k];
    const filtersChanged = first || FILTER_KEYS.some((k) => changed(k));
    if (filtersChanged) {
      compute(s);
      mapApi.setChurches(matched, dimmed);
      panel.resetPaging();
      panel.renderChips(s);
      panel.renderFacets(s, data.churches);
      panel.renderContext(s, matched, data.churches, origin());
      panel.renderList(matched, origin());
      panel.setQuery(s.q);
      const p = parsePlace(s.place);
      mapApi.setActiveNeighborhood(p && p.type === 'nb' ? p.value : null);
      if (p && p.type === 'nb') loadNeighborhoods();
      if (!first && prev && s.place !== prev.place && matched.length) fit();
      if (!first && prev && s.near && s.near !== prev.near && matched.length) fit();
    }
    if (changed('sel')) {
      mapApi.setSelected(s.sel);
      const c = s.sel ? byId.get(s.sel) : null;
      if (c) { drawer.open(c); panel.highlight(c.i); if (!first) ease(c); }
      else drawer.close();
      if (first && c) ease(c);
      if (mobile.matches && c) sheet.peek();
    }
    if (changed('labels')) { mapApi.setLabels(s.labels); syncLayerButtons(s); }
    if (changed('heat')) { mapApi.setHeat(s.heat); syncLayerButtons(s); }
    if (changed('now') && s.now) scheduleNowRefresh();
  }
  store.subscribe((s, prev) => draw(s, prev, false));

  function syncLayerButtons(s) {
    for (const b of document.querySelectorAll('[data-layer]')) b.setAttribute('aria-pressed', String(!!s[b.dataset.layer]));
    $('atlasHeatNote').hidden = !s.heat;
  }
  let nowTimer = null;
  function scheduleNowRefresh() { clearTimeout(nowTimer); nowTimer = setTimeout(() => { if (store.get().now) draw(store.get(), null, true); }, 60000); }

  /* ── camera ──────────────────────────────────────────────────────────── */
  function ease(c) {
    map.easeTo({ center: [c.lng, c.lat], zoom: Math.max(map.getZoom(), 13.5), padding: pad(), duration: 250, essential: true });
  }
  function fit() {
    const b = boundsOf(matched);
    if (!b) return;
    if (matched.length === 1) { map.easeTo({ center: [matched[0].lng, matched[0].lat], zoom: 14, padding: pad(), duration: 300 }); return; }
    map.fitBounds(b, { padding: pad(), duration: 400, maxZoom: 15 });
  }
  map.on('moveend', () => {
    const c = map.getCenter();
    store.set({ view: [Number(map.getZoom().toFixed(2)), Number(c.lat.toFixed(5)), Number(c.lng.toFixed(5))] });
  });

  /* ── selection and picking ───────────────────────────────────────────── */
  function select(id, source) {
    if (id && !byId.has(id)) return;
    store.set({ sel: id || '' });
    if (id && window.trackEvent) window.trackEvent('map_select', { source: source || 'map', church: byId.get(id).n });
  }
  function pick(it) {
    if (it.type === 'church') { store.set({ q: '' }); select(it.church.i, 'search'); return; }
    if (it.type === 'place') { store.set({ q: '', place: `${it.placeType}:${it.value}` }); return; }
    if (it.type === 'lang') { store.set({ q: '', lang: it.code }); return; }
    if (it.type === 'day') { store.set({ q: '', day: it.day }); return; }
    if (it.type === 'facet') {
      const patch = { q: '' };
      if (it.key.startsWith('kind:')) patch.kind = it.key.slice(5); else patch[it.key] = true;
      store.set(patch);
    }
  }
  function zoomCluster(f) {
    mapApi.expandCluster(f).then((z) => map.easeTo({ center: f.geometry.coordinates, zoom: Math.min(z + 0.2, 16), padding: pad(), duration: 300 }));
  }
  function listIds(ids) {
    const set = new Set(ids);
    const subset = matched.filter((c) => set.has(c.i));
    panel.renderList(subset, origin());
    if (mobile.matches) sheet.open();
  }

  mapApi.on('select', (id) => select(id, 'map'));
  mapApi.on('cluster', (f, point) => { if (f.source === 'grid') { map.easeTo({ center: f.geometry.coordinates, zoom: map.getZoom() + 2, duration: 300 }); return; } cluster.show(f, point, true); });
  mapApi.on('clickEmpty', () => { cluster.hide(true); if (mobile.matches && store.get().sel) select(null); });
  mapApi.on('hover', (f, point) => {
    if (!f) { tip.hidden = true; if (!cluster.isPinned()) cluster.hide(false); return; }
    if (f.layer.id === 'points') { cluster.hide(false); tip.textContent = f.properties.n; tip.hidden = false; tip.style.transform = `translate(${point.x + 12}px, ${point.y + 12}px)`; }
    else { tip.hidden = true; if (!cluster.isPinned()) cluster.show(f, point, false); }
  });

  /* ── near me ─────────────────────────────────────────────────────────── */
  let locating = false;
  async function nearMe() {
    if (locating) return;
    locating = true;
    const btn = document.querySelector('[data-locate]'); if (btn) btn.textContent = t('near.locating');
    const pos = await locate();
    locating = false;
    if (!pos) { $('atlasNotice').textContent = t('near.failed'); $('atlasNotice').hidden = false; if (btn) btn.textContent = t('near.me'); return; }
    you = pos;
    mapApi.setYou(pos);
    $('atlasNotice').hidden = pos.source !== 'network';
    if (pos.source === 'network') $('atlasNotice').textContent = t('near.approx');
    const s = store.get();
    store.set({ near: [Number(pos.lat.toFixed(5)), Number(pos.lng.toFixed(5)), s.near ? s.near[2] : 10] });
    if (window.trackEvent) window.trackEvent('map_near_me', { source: pos.source });
  }

  /* ── neighbourhood shapes, LA only, on demand ────────────────────────── */
  let nbLoaded = false;
  function loadNeighborhoods() {
    if (nbLoaded || !data.places.length) return;
    nbLoaded = true;
    fetch('/data/la-neighborhoods.geojson').then((r) => r.json()).then((gj) => {
      mapApi.setNeighborhoods(gj);
      const p = parsePlace(store.get().place);
      mapApi.setActiveNeighborhood(p && p.type === 'nb' ? p.value : null);
    }).catch(() => { nbLoaded = false; });
  }

  /* ── the world, one viewport at a time ───────────────────────────────── */
  let vpTimer = null, vpSeq = 0;
  function loadViewport() {
    clearTimeout(vpTimer);
    vpTimer = setTimeout(async () => {
      const b = map.getBounds(), z = map.getZoom();
      const seq = ++vpSeq;
      const bounds = [b.getSouth(), b.getWest(), b.getNorth(), b.getEast()].map((v) => v.toFixed(3)).join(',');
      try {
        const r = await fetch(`/api/atlas?bounds=${bounds}&z=${Math.floor(z)}`);
        const d = await r.json();
        if (seq !== vpSeq) return;
        if (d.grid) { mapApi.setGrid(d.grid); data.churches = []; }
        else { mapApi.setGrid([]); data.churches = d.churches || []; }
        byId.clear(); for (const c of data.churches) byId.set(c.i, c);
        index = buildIndex(data, t); textMatch = textMatcher(index);
        $('atlasStats').textContent = d.grid ? t('viewport.zoomIn') : statsLine(data);
        draw(store.get(), null, true);
      } catch (_) { /* keep what is drawn */ }
    }, 250);
  }
  if (viewportMode) map.on('moveend', loadViewport);

  /* ── controls ────────────────────────────────────────────────────────── */
  $('atlasZoomIn').addEventListener('click', () => map.zoomIn({ duration: 200 }));
  $('atlasZoomOut').addEventListener('click', () => map.zoomOut({ duration: 200 }));
  $('atlasLocate').addEventListener('click', nearMe);
  $('atlasFit').addEventListener('click', fit);
  $('atlasShare').addEventListener('click', (e) => share(e.currentTarget));
  $('atlasPanelToggle').addEventListener('click', () => { document.body.classList.toggle('atlas-panel-closed'); setTimeout(() => map.resize(), 220); });
  $('atlasPaletteBtn').addEventListener('click', () => palette.toggle());
  $('atlasLayersBtn').addEventListener('click', () => { const m = $('atlasLayers'); m.hidden = !m.hidden; $('atlasLayersBtn').setAttribute('aria-expanded', String(!m.hidden)); });
  $('atlasLayers').addEventListener('click', (e) => { const b = e.target.closest('[data-layer]'); if (b) store.set({ [b.dataset.layer]: !store.get()[b.dataset.layer] }); });
  $('atlasKeysClose').addEventListener('click', () => { $('atlasKeys').hidden = true; });

  function share(btn) {
    const url = location.href;
    const done = () => { const old = btn.getAttribute('aria-label'); btn.classList.add('is-done'); btn.setAttribute('aria-label', t('drawer.copied')); setTimeout(() => { btn.classList.remove('is-done'); btn.setAttribute('aria-label', old); }, 1600); };
    if (navigator.clipboard) navigator.clipboard.writeText(url).then(done, () => prompt(t('drawer.share'), url)); else prompt(t('drawer.share'), url);
  }

  const actions = {
    fit, near: nearMe,
    labels: () => store.set({ labels: !store.get().labels }),
    heat: () => store.set({ heat: !store.get().heat }),
    clear: () => store.set(clearedFilters()),
    share: () => share($('atlasShare')),
    search: () => { if (mobile.matches) sheet.open(); panel.focusSearch(); },
    keys: () => { $('atlasKeys').hidden = !$('atlasKeys').hidden; },
    hasSelection: () => !!store.get().sel,
    open: () => { const c = byId.get(store.get().sel); if (c && c.s) location.href = (lang === 'es' ? '/es' : '') + '/church/' + encodeURIComponent(c.s); },
    directions: () => { const c = byId.get(store.get().sel); if (c) window.open(`https://www.google.com/maps/dir/?api=1&destination=${c.lat},${c.lng}`, '_blank', 'noopener'); },
    escape: () => {
      if (palette.isOpen()) { palette.close(); return; }
      if (!$('atlasKeys').hidden) { $('atlasKeys').hidden = true; return; }
      if (!$('atlasLayers').hidden) { $('atlasLayers').hidden = true; return; }
      if (cluster.isPinned()) { cluster.hide(true); return; }
      if (store.get().sel) { select(null); return; }
      if (mobile.matches && sheet.snap() !== 'peek') sheet.peek();
    },
  };
  const palette = createPalette({
    t, actions, onPick: pick, search: (q, n) => search(index, q, n),
    allLanguages: () => index.langs.map((l) => ({ type: 'lang', code: l.code, label: l.label })),
    topPlaces: () => index.places.slice().sort((a, b) => b.count - a.count).slice(0, 8).map((p) => ({ type: 'place', placeType: p.type, value: p.value, label: p.label, count: p.count })),
  });
  bindKeys({ palette, actions });
  syncLayerButtons(store.get());
  if (pendingSel && !byId.has(pendingSel)) store.set({ sel: '' });
}

function statsLine(data) {
  const n = data.churches.length;
  const langs = new Set(); const regions = new Set();
  for (const c of data.churches) { for (const l of c.l || []) langs.add(l); if (c.r) regions.add(c.r); }
  const parts = [t('results.count', { n: n.toLocaleString(lang) })];
  if (regions.size) parts.push(lang === 'es' ? `${regions.size} regiones` : `${regions.size} regions`);
  if (langs.size > 1) parts.push(lang === 'es' ? `${langs.size} idiomas` : `${langs.size} languages`);
  return parts.join(' · ');
}

function legend() {
  const row = (k) => `<span>${markSvg(k, 14)} ${t('kind.' + k)}</span>`;
  return ['c', 'b', 's', 'p', 'm'].map(row).join('') + `<span class="atlas-legend-dim"><i></i> ${t('legend.dim')}</span>`;
}

boot();
export { LANGUAGE_NAMES };
